if (!customElements.get('resource-search')) {
  class ResourceSearch extends HTMLElement {
    constructor() {
      super();
      this.handleDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
      this.handleDocumentFocusIn = this.handleDocumentFocusIn.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
      this.handleInput = this.handleInput.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleReset = this.handleReset.bind(this);
      this.handleWindowBlur = this.handleWindowBlur.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.form = this.querySelector('[data-resource-search-form]');
      this.input = this.querySelector('[data-resource-search-input]');
      this.panel = this.querySelector('[data-resource-search-panel]');
      this.results = this.querySelector('[data-resource-search-results]');
      this.status = this.querySelector('[data-resource-search-status]');
      this.emptyState = this.querySelector('[data-resource-search-empty]');
      this.resetButton = this.querySelector('[data-resource-search-reset]');
      this.dataElements = Array.from(this.querySelectorAll('[data-resource-search-data]'));

      if (!this.form || !this.input || !this.panel || !this.results || !this.dataElements.length) return;

      this.items = [];
      this.dataElements.forEach((dataElement) => {
        try {
          const chunk = JSON.parse(dataElement.textContent);
          if (Array.isArray(chunk)) {
            this.items = this.items.concat(chunk);
          }
        } catch (error) {
          // Keep the search usable even if one generated chunk fails to parse.
        }
      });

      this.defaultResults = this.items.filter((item) => item.type.toLowerCase().includes('glossary')).slice(0, 6);
      if (!this.defaultResults.length) {
        this.defaultResults = this.items.slice(0, 6);
      }

      this.currentResults = [];
      this.activeIndex = -1;

      this.input.addEventListener('focus', this.handleInput);
      this.input.addEventListener('input', this.handleInput);
      this.input.addEventListener('keydown', this.handleKeydown);
      this.form.addEventListener('submit', this.handleSubmit);
      this.form.addEventListener('reset', this.handleReset);
      this.addEventListener('focusout', this.handleFocusOut);
      document.addEventListener('pointerdown', this.handleDocumentPointerDown, true);
      document.addEventListener('focusin', this.handleDocumentFocusIn, true);
      window.addEventListener('blur', this.handleWindowBlur);
    }

    disconnectedCallback() {
      document.removeEventListener('pointerdown', this.handleDocumentPointerDown, true);
      document.removeEventListener('focusin', this.handleDocumentFocusIn, true);
      window.removeEventListener('blur', this.handleWindowBlur);
    }

    handleInput() {
      this.toggleResetButton();
      this.renderResults(this.input.value.trim());
      this.open();
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }

      if (this.panel.hidden) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.setActiveIndex(this.activeIndex < this.currentResults.length - 1 ? this.activeIndex + 1 : 0);
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.setActiveIndex(this.activeIndex > 0 ? this.activeIndex - 1 : this.currentResults.length - 1);
      }

      if (event.key === 'Enter' && this.activeIndex >= 0 && this.currentResults[this.activeIndex]) {
        event.preventDefault();
        window.location.assign(this.currentResults[this.activeIndex].url);
      }
    }

    handleSubmit(event) {
      const query = this.input.value.trim();

      if (!query.length) {
        event.preventDefault();
        this.renderResults('');
        this.open();
        return;
      }

      if (this.currentResults.length) {
        event.preventDefault();
        const targetResult = this.currentResults[this.activeIndex >= 0 ? this.activeIndex : 0];
        window.location.assign(targetResult.url);
      }
    }

    handleReset(event) {
      event.preventDefault();
      this.input.value = '';
      this.toggleResetButton();
      this.renderResults('');
      this.open();
      this.input.focus();
    }

    handleDocumentPointerDown(event) {
      if (this.panel.hidden || this.isInsideComponent(event)) return;

      this.close();

      if (document.activeElement === this.input) {
        this.input.blur();
      }
    }

    handleDocumentFocusIn(event) {
      if (this.panel.hidden || this.isInsideComponent(event)) return;

      this.close();
    }

    handleFocusOut() {
      window.setTimeout(() => {
        if (!this.isInsideComponent(document.activeElement)) {
          this.close();
        }
      }, 0);
    }

    handleWindowBlur() {
      this.close();
    }

    toggleResetButton() {
      if (!this.resetButton) return;

      if (this.input.value.trim().length) {
        this.resetButton.classList.remove('hidden');
      } else {
        this.resetButton.classList.add('hidden');
      }
    }

    open() {
      if (!this.panel.hidden) return;

      this.setElementHidden(this.panel, false);
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      if (this.panel.hidden) return;

      this.setElementHidden(this.panel, true);
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-activedescendant', '');
      this.activeIndex = -1;
    }

    setElementHidden(element, hidden) {
      if (!element) return;

      element.hidden = hidden;
      element.classList.toggle('hidden', hidden);
    }

    isInsideComponent(target) {
      if (!target) return false;

      if (typeof Event !== 'undefined' && target instanceof Event) {
        const eventPath = typeof target.composedPath === 'function' ? target.composedPath() : [];
        return eventPath.length ? eventPath.includes(this) : this.contains(target.target);
      }

      return target === this || this.contains(target);
    }

    renderResults(query) {
      const trimmedQuery = query.trim();
      const matchingResults = trimmedQuery ? this.search(trimmedQuery) : this.defaultResults;
      this.currentResults = matchingResults.slice(0, 7);
      this.activeIndex = -1;

      if (!this.currentResults.length) {
        this.results.innerHTML = '';
        this.setElementHidden(this.emptyState, false);
        this.status.textContent = trimmedQuery
          ? 'No matching resources yet'
          : 'Popular glossary terms and resource paths';
        return;
      }

      this.setElementHidden(this.emptyState, true);
      this.status.textContent = trimmedQuery
        ? `${this.currentResults.length} matching resource${this.currentResults.length === 1 ? '' : 's'}`
        : 'Popular glossary terms and resource paths';

      this.results.innerHTML = this.currentResults
        .map((item, index) => {
          const optionId = `${this.results.id || 'resource-search-option'}-${index}`;
          return `
            <li class="resource-search__result" role="option" aria-selected="false">
              <a
                href="${this.escapeHtml(item.url)}"
                class="resource-search__option"
                id="${optionId}"
                data-resource-search-option
                data-index="${index}"
              >
                <span class="resource-search__badge">${this.escapeHtml(item.type)}</span>
                <span class="resource-search__option-title">${this.highlightText(item.title, trimmedQuery)}</span>
                <span class="resource-search__option-description">${this.escapeHtml(item.description)}</span>
              </a>
            </li>
          `;
        })
        .join('');

      this.results.querySelectorAll('[data-resource-search-option]').forEach((option) => {
        option.addEventListener('mouseenter', () => {
          this.setActiveIndex(Number(option.dataset.index));
        });
      });
    }

    setActiveIndex(index) {
      if (!this.currentResults.length) return;

      const options = this.results.querySelectorAll('[data-resource-search-option]');
      if (!options.length) return;

      this.activeIndex = index;
      options.forEach((option, optionIndex) => {
        const isActive = optionIndex === index;
        option.classList.toggle('is-active', isActive);
        option.parentElement.setAttribute('aria-selected', isActive ? 'true' : 'false');
        if (isActive) {
          this.input.setAttribute('aria-activedescendant', option.id);
        }
      });
    }

    search(query) {
      const normalizedQuery = query.toLowerCase();
      const tokens = normalizedQuery.split(/[\s/-]+/).filter(Boolean);

      return this.items
        .map((item) => {
          const title = item.title.toLowerCase();
          const type = item.type.toLowerCase();
          const description = item.description.toLowerCase();
          const keywords = item.keywords.toLowerCase();
          const haystack = `${title} ${type} ${description} ${keywords}`;

          if (!tokens.every((token) => haystack.includes(token))) {
            return null;
          }

          let score = 0;

          if (title === normalizedQuery) score += 120;
          if (title.startsWith(normalizedQuery)) score += 80;
          if (title.includes(normalizedQuery)) score += 40;
          if (keywords.includes(normalizedQuery)) score += 25;
          if (type.includes(normalizedQuery)) score += 18;

          tokens.forEach((token) => {
            if (title.startsWith(token)) score += 18;
            if (title.includes(token)) score += 10;
            if (keywords.includes(token)) score += 7;
            if (description.includes(token)) score += 4;
            if (type.includes(token)) score += 3;
          });

          return {
            ...item,
            score,
          };
        })
        .filter(Boolean)
        .sort((left, right) => {
          if (right.score !== left.score) {
            return right.score - left.score;
          }
          return left.rank - right.rank;
        });
    }

    highlightText(text, query) {
      const escapedText = this.escapeHtml(text);

      if (!query) return escapedText;

      const tokens = query
        .toLowerCase()
        .split(/[\s/-]+/)
        .filter(Boolean)
        .sort((left, right) => right.length - left.length);

      if (!tokens.length) return escapedText;

      return tokens.reduce((output, token) => {
        const pattern = new RegExp(`(${this.escapeRegExp(token)})`, 'gi');
        return output.replace(pattern, '<mark>$1</mark>');
      }, escapedText);
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    escapeRegExp(value) {
      return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
  }

  customElements.define('resource-search', ResourceSearch);
}
